import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morphSection, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, prefersReducedMotion } from '@theme/utilities';

const PRODUCT_SECTION_MARKER = '[data-variant-refresh-section]';
const PRODUCT_PAGE_PICKER = 'variant-picker[data-template-product-match="true"]';
const PREFETCH_DELAY_MS = 100;
const SECONDARY_SECTION_CHUNK_SIZE = 5;

let productPageRequestSequence = 0;
let initialProductSwatchOrder;
/** @type {AbortController | undefined} */
let activeProductPageAbortController;
/** @type {Map<string, number>} */
const secondarySectionFailures = new Map();

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  #lifecycleController = new AbortController();

  /** @type {number | undefined} */
  #intentTimer;

  /** @type {HTMLElement | undefined} */
  #intentOption;

  /** @type {string[]} */
  #confirmedOptionIds = [];

  /** @type {Object | null} */
  #confirmedVariant = null;

  #confirmedUrl = '';

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#preserveInitialProductSwatchOrder();
    this.#lifecycleController.abort();
    this.#lifecycleController = new AbortController();
    this.#initializeOptionState();
    this.#rememberConfirmedState();

    const { signal } = this.#lifecycleController;
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('pointerover', this.#handlePrefetchIntent, { signal });
    this.addEventListener('focusin', this.#handlePrefetchIntent, { signal });
    this.addEventListener('pointerdown', this.#handleImmediatePrefetch, { signal });
    this.addEventListener('pointerout', this.#handlePrefetchEnd, { signal });
    this.addEventListener('focusout', this.#handlePrefetchEnd, { signal });
    this.#resizeObserver.observe(this);
    this.#scrollCurrentSwatchesIntoView('instant');
  }

  #preserveInitialProductSwatchOrder() {
    if (this.dataset.templateProductMatch !== 'true') return;

    const fieldset = Array.from(this.querySelectorAll('.variant-option--swatches')).find((option) =>
      option.querySelector('input[data-connected-product-url]:not([data-connected-product-url=""])')
    );
    const inputs = Array.from(fieldset?.querySelectorAll('input[data-option-value-id]') ?? []);
    const order = inputs.map((input) => {
      const optionValueId = input.getAttribute('data-option-value-id');
      const productUrl = input.getAttribute('data-connected-product-url') || this.dataset.productUrl;
      const productPath = productUrl ? new URL(productUrl, window.location.origin).pathname : '';
      return productPath && optionValueId ? `${productPath}:${optionValueId}` : '';
    });

    if (inputs.length < 2 || order.some((swatchKey) => !swatchKey)) return;
    if (!initialProductSwatchOrder) {
      initialProductSwatchOrder = order;
      return;
    }

    const parent = inputs[0]?.closest('overflow-list');
    if (!parent) return;

    initialProductSwatchOrder.forEach((swatchKey) => {
      const input = inputs[order.indexOf(swatchKey)];
      const swatch = input?.closest('.variant-option__swatch');
      if (swatch) parent.append(swatch);
    });

    parent.querySelectorAll('input[data-input-index]').forEach((input, index) => {
      if (input instanceof HTMLInputElement) input.dataset.inputIndex = String(index);
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#preserveInitialProductSwatchOrder();
    this.#initializeOptionState();
    this.#scrollCurrentSwatchesIntoView('instant');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#lifecycleController.abort();
    if (this.#intentTimer !== undefined) clearTimeout(this.#intentTimer);
    this.#resizeObserver.disconnect();
  }

  /** @param {Event} event */
  #handleChange = (event) => this.variantChanged(event);

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.#parseProductUrl(this.dataset.productUrl)?.pathname;
    const connectedProductUrl = this.#parseProductUrl(selectedOption.dataset.connectedProductUrl);
    const connectedProductPath = connectedProductUrl?.pathname;
    const loadsNewProduct = isOnProductPage && !!connectedProductPath && connectedProductPath !== currentUrl;
    const loadsDifferentProductTemplate = loadsNewProduct && this.#loadsDifferentProductTemplate(selectedOption);
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const requestUrl = this.buildRequestUrl(selectedOption);
    const url = new URL(window.location.href);
    const variantId = this.#getSelectedVariantId(selectedOption);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct && connectedProductPath) {
      url.pathname = connectedProductPath;
    }

    if (isOnProductPage) {
      this.#loadProductPageVariant(requestUrl, url, loadsNewProduct, loadsDifferentProductTemplate);
      return;
    }

    const morphElementSelector = isOnFeaturedProductSection ? 'featured-product-information' : undefined;
    this.fetchUpdatedSection(requestUrl, morphElementSelector);

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #initializeOptionState() {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });
  }

  /** @param {Event} event */
  #handlePrefetchIntent = (event) => {
    const option = this.#getOptionFromEvent(event);
    if (!option) return;

    this.#queueVariantPrefetch(option, PREFETCH_DELAY_MS);
  };

  /** @param {Event} event */
  #handleImmediatePrefetch = (event) => {
    const option = this.#getOptionFromEvent(event);
    if (!option) return;

    this.#queueVariantPrefetch(option, 0);
  };

  /** @param {Event} event */
  #handlePrefetchEnd = (event) => {
    const option = this.#getOptionFromEvent(event);
    if (!option || option !== this.#intentOption) return;

    const relatedTarget = /** @type {FocusEvent | MouseEvent} */ (event).relatedTarget;
    const optionContainer = option.closest('label, .variant-option__select-wrapper') || option;
    if (relatedTarget instanceof Node && optionContainer.contains(relatedTarget)) return;

    if (this.#intentTimer !== undefined) clearTimeout(this.#intentTimer);
    this.#intentTimer = undefined;
    this.#intentOption = undefined;
  };

  /**
   * Finds the actual input/option represented by a pointer or focus event.
   * @param {Event} event
   * @returns {HTMLElement | undefined}
   */
  #getOptionFromEvent(event) {
    if (!(event.target instanceof Element)) return undefined;

    if (event.target instanceof HTMLSelectElement) {
      return event.target.options[event.target.selectedIndex];
    }

    const directOption = event.target.closest('[data-option-value-id]');
    if (directOption instanceof HTMLElement) return directOption;

    const labelledInput = event.target.closest('label')?.querySelector('[data-option-value-id]');
    return labelledInput instanceof HTMLElement ? labelledInput : undefined;
  }

  /**
   * Schedules a bounded intent prefetch for a likely next variant.
   * @param {HTMLElement} option
   * @param {number} delay
   */
  #queueVariantPrefetch(option, delay) {
    if (!this.#canPrefetch() || option.dataset.optionValueId === this.selectedOptionId) return;

    if (this.#intentTimer !== undefined) clearTimeout(this.#intentTimer);
    this.#intentOption = option;

    const run = () => {
      this.#intentTimer = undefined;
      this.#prefetchVariant(option);
    };

    if (delay === 0) {
      run();
    } else {
      this.#intentTimer = setTimeout(run, delay);
    }
  }

  /** @returns {boolean} */
  #canPrefetch() {
    if (this.dataset.templateProductMatch !== 'true' || Shopify.designMode) return false;

    const connection = /** @type {Navigator & {connection?: {saveData?: boolean, effectiveType?: string}}} */ (navigator)
      .connection;
    if (connection?.saveData) return false;
    if (connection?.effectiveType?.includes('2g')) return false;

    return true;
  }

  /** @param {HTMLElement} option */
  #prefetchVariant(option) {
    const currentProductPath = this.#parseProductUrl(this.dataset.productUrl)?.pathname;
    const connectedProductPath = this.#parseProductUrl(option.dataset.connectedProductUrl)?.pathname;
    const loadsNewProduct = Boolean(connectedProductPath && connectedProductPath !== currentProductPath);
    if (loadsNewProduct && this.#loadsDifferentProductTemplate(option)) return;

    const intendedValues = this.#getIntendedOptionValues(option);
    const requestUrl = new URL(this.buildRequestUrl(option, null, [], intendedValues), window.location.origin);
    const descriptor = this.#getProductRefreshDescriptor();

    const prefetch = loadsNewProduct
      ? sectionRenderer.getSectionsHTML(descriptor.primarySectionIds, { url: requestUrl, cache: true })
      : sectionRenderer.getSectionHTML(descriptor.mainSectionId, true, requestUrl);

    prefetch.catch(() => {
      // Intent prefetch is opportunistic. The committed change has its own fallback path.
    });
  }

  /** @param {HTMLElement} option @returns {boolean} */
  #loadsDifferentProductTemplate(option) {
    const currentTemplate = this.dataset.templateSuffix || 'default';
    const connectedTemplate = option.dataset.connectedProductTemplate;

    // An unknown destination uses the full document path so template correctness wins.
    return !connectedTemplate || connectedTemplate !== currentTemplate;
  }

  /**
   * Returns the option value IDs that would be selected if the intent target were committed.
   * @param {HTMLElement} option
   * @returns {string[]}
   */
  #getIntendedOptionValues(option) {
    const values = [...this.selectedOptionsValues];
    const fieldset = option.closest('fieldset');
    const fieldsetIndex = Number.parseInt(option.dataset.fieldsetIndex || fieldset?.dataset.fieldsetIndex || '');
    const optionValueId = option.dataset.optionValueId;

    if (!Number.isNaN(fieldsetIndex) && optionValueId) values[fieldsetIndex] = optionValueId;
    return values;
  }

  /**
   * Discovers the template's critical and secondary refresh sections from DOM markers.
   * @returns {{mainSectionId: string, primarySectionIds: string[], secondarySectionIds: string[]}}
   */
  #getProductRefreshDescriptor() {
    const mainSection = this.closest('.shopify-section');
    if (!(mainSection instanceof HTMLElement) || !mainSection.id) {
      throw new Error('Product information section not found');
    }

    const mainSectionId = normalizeSectionId(mainSection.id);
    /** @type {string[]} */
    const primarySectionIds = [];
    /** @type {string[]} */
    const secondarySectionIds = [];

    for (const marker of document.querySelectorAll(PRODUCT_SECTION_MARKER)) {
      if (!(marker instanceof HTMLElement)) continue;
      const section = marker.closest('.shopify-section');
      if (!(section instanceof HTMLElement) || !section.id) continue;

      const sectionId = normalizeSectionId(section.id);
      if (marker.dataset.variantRefreshSection === 'primary' && sectionId !== mainSectionId) {
        primarySectionIds.push(sectionId);
      } else if (marker.dataset.variantRefreshSection === 'secondary') {
        secondarySectionIds.push(sectionId);
      }
    }

    // Morph the product information section last. It replaces this picker for combined products.
    primarySectionIds.push(mainSectionId);

    return {
      mainSectionId,
      primarySectionIds: [...new Set(primarySectionIds)],
      secondarySectionIds: [...new Set(secondarySectionIds)].filter(
        (sectionId) => sectionId !== mainSectionId && !primarySectionIds.includes(sectionId)
      ),
    };
  }

  /**
   * Loads and commits the next PDP state. Same-product variants render only the
   * main product section; combined products render critical sections first.
   * @param {string} requestUrl
   * @param {URL} browserUrl
   * @param {boolean} loadsNewProduct
   * @param {boolean} loadsDifferentProductTemplate
   */
  async #loadProductPageVariant(requestUrl, browserUrl, loadsNewProduct, loadsDifferentProductTemplate) {
    const requestSequence = ++productPageRequestSequence;
    activeProductPageAbortController?.abort();
    const abortController = new AbortController();
    activeProductPageAbortController = abortController;
    const descriptor = this.#getProductRefreshDescriptor();
    const productRequestUrl = new URL(requestUrl, window.location.origin);
    const previousProductId = this.dataset.productId ?? '';
    const previousPickerTop = this.getBoundingClientRect().top;
    const previousScrollTop = window.scrollY;
    let usedFullDocument = false;

    this.#setBusy(true);

    try {
      try {
        if (loadsDifferentProductTemplate) {
          usedFullDocument = true;
          await this.#renderFullProductDocument(
            productRequestUrl,
            browserUrl,
            loadsNewProduct,
            previousProductId,
            previousPickerTop,
            previousScrollTop,
            requestSequence,
            abortController.signal
          );
        } else if (loadsNewProduct) {
          await this.#renderCombinedProductSections(
            descriptor,
            productRequestUrl,
            browserUrl,
            previousProductId,
            previousPickerTop,
            previousScrollTop,
            requestSequence,
            abortController.signal
          );
        } else {
          await this.#renderProductVariantSection(
            descriptor.mainSectionId,
            productRequestUrl,
            browserUrl,
            previousProductId,
            requestSequence,
            abortController.signal
          );
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') throw error;
        if (requestSequence !== productPageRequestSequence) return;

        usedFullDocument = true;
        await this.#renderFullProductDocument(
          productRequestUrl,
          browserUrl,
          loadsNewProduct,
          previousProductId,
          previousPickerTop,
          previousScrollTop,
          requestSequence,
          abortController.signal
        );
      }

      if (
        loadsNewProduct &&
        !usedFullDocument &&
        descriptor.secondarySectionIds.length > 0 &&
        requestSequence === productPageRequestSequence
      ) {
        this.#refreshSecondarySections(
          descriptor.secondarySectionIds,
          productRequestUrl,
          requestSequence,
          abortController.signal
        );
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (requestSequence !== productPageRequestSequence) return;

      console.error('Unable to update product variant:', error);
      this.#restoreConfirmedState();
    } finally {
      if (requestSequence === productPageRequestSequence) {
        const activePicker = document.querySelector(PRODUCT_PAGE_PICKER);
        if (activePicker instanceof VariantPicker) activePicker.#setBusy(false);
      }
    }
  }

  /**
   * @param {string} mainSectionId
   * @param {URL} productRequestUrl
   * @param {URL} browserUrl
   * @param {string} previousProductId
   * @param {number} requestSequence
   * @param {AbortSignal} signal
   */
  async #renderProductVariantSection(
    mainSectionId,
    productRequestUrl,
    browserUrl,
    previousProductId,
    requestSequence,
    signal
  ) {
    const sectionHTML = await sectionRenderer.getSectionHTML(mainSectionId, true, productRequestUrl, signal);
    if (requestSequence !== productPageRequestSequence) return;

    const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
    const parsedVariant = this.#parseVariant(html);
    const newProduct = this.updateVariantPicker(html);
    this.#ensureSelectionState();

    this.#commitProductPageUpdate(this, html, parsedVariant, browserUrl, previousProductId, newProduct);
  }

  /**
   * @param {{mainSectionId: string, primarySectionIds: string[]}} descriptor
   * @param {URL} productRequestUrl
   * @param {URL} browserUrl
   * @param {string} previousProductId
   * @param {number} previousPickerTop
   * @param {number} previousScrollTop
   * @param {number} requestSequence
   * @param {AbortSignal} signal
   */
  async #renderCombinedProductSections(
    descriptor,
    productRequestUrl,
    browserUrl,
    previousProductId,
    previousPickerTop,
    previousScrollTop,
    requestSequence,
    signal
  ) {
    const sections = await sectionRenderer.getSectionsHTML(descriptor.primarySectionIds, {
      url: productRequestUrl,
      cache: true,
      signal,
    });
    if (requestSequence !== productPageRequestSequence) return;

    const html = this.#createSectionsDocument(sections, descriptor.primarySectionIds);
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
    const parsedVariant = this.#parseVariant(html);

    for (const sectionId of descriptor.primarySectionIds) {
      await morphSection(sectionId, sections[sectionId], {
        injectStylesheet: true,
        ...(sectionId === descriptor.mainSectionId ? { getNodeKey: getProductPageNodeKey } : {}),
      });
    }

    if (requestSequence !== productPageRequestSequence) return;

    const activePicker = document.querySelector(PRODUCT_PAGE_PICKER);
    if (!(activePicker instanceof VariantPicker)) throw new Error('Updated product variant picker not found');

    activePicker.#ensureSelectionState();
    this.#restorePickerViewportPosition(activePicker, previousPickerTop, previousScrollTop);
    const newProductId = activePicker.dataset.productId ?? '';
    const newProductUrl = activePicker.dataset.productUrl ?? '';
    const newProduct =
      newProductId && newProductUrl && newProductId !== previousProductId
        ? { id: newProductId, url: newProductUrl }
        : undefined;

    this.#commitProductPageUpdate(
      activePicker,
      html,
      parsedVariant,
      browserUrl,
      previousProductId,
      newProduct,
      'main'
    );
  }

  /**
   * Uses the destination product's complete document when its template differs
   * or when section rendering fails.
   * @param {URL} productRequestUrl
   * @param {URL} browserUrl
   * @param {boolean} loadsNewProduct
   * @param {string} previousProductId
   * @param {number} previousPickerTop
   * @param {number} previousScrollTop
   * @param {number} requestSequence
   * @param {AbortSignal} signal
   */
  async #renderFullProductDocument(
    productRequestUrl,
    browserUrl,
    loadsNewProduct,
    previousProductId,
    previousPickerTop,
    previousScrollTop,
    requestSequence,
    signal
  ) {
    const response = await fetch(productRequestUrl, { signal });
    if (!response.ok) throw new Error(`Product rendering failed (${response.status})`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (requestSequence !== productPageRequestSequence) return;
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
    const parsedVariant = this.#parseVariant(html);

    if (loadsNewProduct) {
      if (this.contains(document.activeElement) && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      this.updateMain(html);
      this.#syncTemplateClasses(html);
      const activePicker = document.querySelector(PRODUCT_PAGE_PICKER);
      if (!(activePicker instanceof VariantPicker)) throw new Error('Updated product variant picker not found');

      const sourcePicker = html.querySelector(PRODUCT_PAGE_PICKER);
      if (sourcePicker instanceof HTMLElement) activePicker.#syncSelectionFromSource(sourcePicker);
      activePicker.#ensureSelectionState();
      this.#restorePickerViewportPosition(activePicker, previousPickerTop, previousScrollTop);

      const newProductId = activePicker.dataset.productId ?? '';
      const newProductUrl = activePicker.dataset.productUrl ?? '';
      const newProduct =
        newProductId && newProductUrl && newProductId !== previousProductId
          ? { id: newProductId, url: newProductUrl }
          : undefined;

      this.#commitProductPageUpdate(
        activePicker,
        html,
        parsedVariant,
        browserUrl,
        previousProductId,
        newProduct,
        'main'
      );
      return;
    }

    const newProduct = this.updateVariantPicker(html);
    this.#ensureSelectionState();
    this.#commitProductPageUpdate(this, html, parsedVariant, browserUrl, previousProductId, newProduct);
  }

  /**
   * @param {VariantPicker} activePicker
   * @param {Document} html
   * @param {Object | null} parsedVariant
   * @param {URL} browserUrl
   * @param {string} previousProductId
   * @param {{id: string, url: string} | undefined} newProduct
   * @param {string} [morphTarget]
   */
  #commitProductPageUpdate(
    activePicker,
    html,
    parsedVariant,
    browserUrl,
    previousProductId,
    newProduct,
    morphTarget
  ) {
    const metadata = html.querySelector('[data-product-page-metadata]');
    if (metadata instanceof HTMLElement) {
      const title = metadata.dataset.documentTitle;
      const canonicalUrl = metadata.dataset.canonicalUrl;
      if (title) document.title = title;
      if (canonicalUrl) document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
    } else {
      this.#syncDocumentTitle(html);
    }

    history.replaceState({}, '', browserUrl.toString());

    const currentProductId = activePicker.dataset.productId ?? previousProductId;
    if (newProduct?.id) RecentlyViewed.addProduct(newProduct.id);
    activePicker.#rememberConfirmedState(parsedVariant, browserUrl.toString());
    activePicker.#scrollCurrentSwatchesIntoView(prefersReducedMotion() ? 'instant' : 'smooth');

    if (activePicker.selectedOptionId) {
      activePicker.dispatchEvent(
        new VariantUpdateEvent(parsedVariant, activePicker.selectedOptionId, {
          html,
          productId: currentProductId,
          newProduct,
          morphTarget,
        })
      );
    }
  }

  /**
   * Refreshes product-context widgets only after the critical product UI has painted.
   * @param {string[]} sectionIds
   * @param {URL} productRequestUrl
   * @param {number} requestSequence
   * @param {AbortSignal} signal
   */
  async #refreshSecondarySections(sectionIds, productRequestUrl, requestSequence, signal) {
    await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
    await yieldToMainThread();
    if (requestSequence !== productPageRequestSequence || signal.aborted) return;

    for (let index = 0; index < sectionIds.length; index += SECONDARY_SECTION_CHUNK_SIZE) {
      const sectionChunk = sectionIds.slice(index, index + SECONDARY_SECTION_CHUNK_SIZE);

      try {
        const sections = await sectionRenderer.getSectionsHTML(sectionChunk, {
          url: productRequestUrl,
          cache: true,
          signal,
        });
        if (requestSequence !== productPageRequestSequence) return;

        for (const sectionId of sectionChunk) {
          await morphSection(sectionId, sections[sectionId], { injectStylesheet: true });
          secondarySectionFailures.delete(sectionId);
          const section = document.getElementById(`shopify-section-${sectionId}`);
          if (section instanceof HTMLElement) section.hidden = false;
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;

        for (const sectionId of sectionChunk) {
          const failureCount = (secondarySectionFailures.get(sectionId) ?? 0) + 1;
          secondarySectionFailures.set(sectionId, failureCount);
          if (failureCount >= 2) {
            const section = document.getElementById(`shopify-section-${sectionId}`);
            if (section instanceof HTMLElement) section.hidden = true;
          }
        }
      }
    }
  }

  /**
   * @param {Record<string, string>} sections
   * @param {string[]} order
   * @returns {Document}
   */
  #createSectionsDocument(sections, order) {
    return new DOMParser().parseFromString(order.map((sectionId) => sections[sectionId]).join(''), 'text/html');
  }

  /** @param {Document} html @returns {Object | null} */
  #parseVariant(html) {
    const textContent = html.querySelector(`${PRODUCT_PAGE_PICKER} script[type="application/json"]`)?.textContent;
    if (!textContent) throw new Error('Rendered variant data not found');

    return JSON.parse(textContent);
  }

  /** @param {Object | null} [variant] @param {string} [url] */
  #rememberConfirmedState(variant, url = window.location.href) {
    this.#confirmedOptionIds = [...this.selectedOptionsValues];
    this.#confirmedUrl = url;

    if (variant !== undefined) {
      this.#confirmedVariant = variant;
      return;
    }

    const textContent = this.querySelector('script[type="application/json"]')?.textContent;
    if (!textContent) return;

    try {
      this.#confirmedVariant = JSON.parse(textContent);
    } catch {
      this.#confirmedVariant = null;
    }
  }

  #restoreConfirmedState() {
    for (const optionValueId of this.#confirmedOptionIds) {
      const option = this.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (option instanceof HTMLInputElement) {
        this.updateSelectedOption(option);
      } else if (option instanceof HTMLOptionElement) {
        const select = option.closest('select');
        if (select) {
          select.value = option.value;
          this.updateSelectedOption(select);
        }
      }
    }

    if (this.#confirmedUrl && window.location.href !== this.#confirmedUrl) {
      history.replaceState({}, '', this.#confirmedUrl);
    }

    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdateEvent(this.#confirmedVariant, this.selectedOptionId, {
          html: document,
          productId: this.dataset.productId ?? '',
        })
      );
    }
  }

  /** @param {boolean} isBusy */
  #setBusy(isBusy) {
    this.setAttribute('aria-busy', String(isBusy));
    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) section.setAttribute('aria-busy', String(isBusy));
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Scrolls the selected swatch into view inside a disabled overflow-list row.
   * @param {HTMLFieldSetElement} fieldset
   * @param {HTMLInputElement} input
   * @param {ScrollBehavior} [behavior]
   */
  #scrollSelectedSwatchIntoView(fieldset, input, behavior = prefersReducedMotion() ? 'instant' : 'smooth') {
    if (!fieldset.classList.contains('variant-option--swatches')) return;

    const overflowList = fieldset.querySelector('overflow-list[disabled]');
    if (!(overflowList instanceof OverflowList)) return;

    const swatchItem = input.closest('.variant-option__swatch');
    if (!(swatchItem instanceof HTMLElement)) return;

    overflowList.scrollToChild(swatchItem, {
      inline: 'center',
      behavior,
    });
  }

  /**
   * Keeps checked swatches visible after initial render, morph, or product switch.
   * @param {ScrollBehavior} [behavior]
   * @param {number} [attempt]
   */
  #scrollCurrentSwatchesIntoView(behavior = 'smooth', attempt = 0) {
    requestAnimationFrame(() => {
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

      if (!fieldsets.length && this.isConnected && attempt < 4) {
        this.#scrollCurrentSwatchesIntoView(behavior, attempt + 1);
        return;
      }

      for (const fieldset of fieldsets) {
        if (!fieldset.classList.contains('variant-option--swatches')) continue;

        const checked = fieldset.querySelector('input:checked');
        if (checked instanceof HTMLInputElement) {
          this.#scrollSelectedSwatchIntoView(fieldset, checked, behavior);
        }
      }
    });
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          for (const index of checkedIndices) {
            if (radios[index]) {
              radios[index].dataset.currentChecked = 'false';
              radios[index].dataset.previousChecked = 'false';
            }
          }

          const nextCheckedIndices = [
            inputIndex,
            ...checkedIndices.filter((index) => index !== inputIndex),
          ].slice(0, 2);
          checkedIndices.splice(0, checkedIndices.length, ...nextCheckedIndices);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
          this.#scrollSelectedSwatchIntoView(fieldset, target);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string[]} [selectedOptionsValues] - Selected values override used by intent prefetch.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
    selectedOptionsValues = this.selectedOptionsValues
  ) {
    const productUrl = selectedOption.dataset.connectedProductUrl || this.dataset.productUrl;
    const params = new URLSearchParams();
    const viewParamValue = getViewParameterValue();
    const currentProductUrl = this.#parseProductUrl(this.dataset.productUrl)?.pathname;
    const connectedProductUrl = this.#parseProductUrl(selectedOption.dataset.connectedProductUrl)?.pathname;
    const isProductSwitch = Boolean(connectedProductUrl && connectedProductUrl !== currentProductUrl);

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.set('view', viewParamValue);

    // Combined listing product switches must not carry option_values from the previous product.
    if (!isProductSwitch && selectedOptionsValues.length && !source) {
      params.set('option_values', selectedOptionsValues.join(','));
    } else if (source === 'product-card') {
      if (selectedOptionsValues.length) {
        params.set('option_values', sourceSelectedOptionsValues.join(','));
      } else {
        params.set('option_values', selectedOption.dataset.optionValueId ?? '');
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      const sectionId = SECTION_ID_MAP[closestSectionId];
      if (sectionId) params.set('section_id', sectionId);
    }

    return this.#buildProductRequestUrl(productUrl, params);
  }

  /**
   * Parses product URLs that can be either path-only or full URLs with query parameters.
   * @param {string | undefined} productUrl
   * @returns {URL | undefined}
   */
  #parseProductUrl(productUrl) {
    if (!productUrl) return undefined;

    return new URL(productUrl, window.location.origin);
  }

  /**
   * Gets the selected variant ID, including combined listing URLs that carry it as ?variant=.
   * @param {HTMLElement} selectedOption
   * @returns {string | null}
   */
  #getSelectedVariantId(selectedOption) {
    const variantId = selectedOption.dataset.variantId;
    if (variantId) return variantId;

    const connectedProductUrl = this.#parseProductUrl(selectedOption.dataset.connectedProductUrl);
    return connectedProductUrl?.searchParams.get('variant') || null;
  }

  /**
   * Builds a relative product request URL while preserving any query params from the product URL.
   * @param {string | undefined} productUrl
   * @param {URLSearchParams} params
   * @returns {string}
   */
  #buildProductRequestUrl(productUrl, params) {
    const url = this.#parseProductUrl(productUrl) || new URL(window.location.href);

    params.forEach((value, key) => {
      if (value) url.searchParams.set(key, value);
    });

    return `${url.pathname}${url.search}${url.hash}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;
        /** @type {this} */
        let activeVariantPicker = this;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          this.#syncDocumentTitle(html);
          const morphedVariantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
          const morphedVariantPickerSource = html.querySelector('variant-picker[data-template-product-match="true"]');

          if (morphedVariantPicker instanceof HTMLElement && morphedVariantPicker.tagName.toLowerCase() === 'variant-picker') {
            activeVariantPicker = /** @type {this} */ (morphedVariantPicker);

            if (morphedVariantPickerSource instanceof HTMLElement) {
              activeVariantPicker.#syncSelectionFromSource(morphedVariantPickerSource);
            }

            const newProductId = morphedVariantPicker.dataset.productId;
            const newProductUrl = morphedVariantPicker.dataset.productUrl;

            if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
              newProduct = { id: newProductId, url: newProductUrl };
            }
          }
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        activeVariantPicker.#ensureSelectionState();

        if (morphElementSelector === 'main') {
          await Promise.resolve();
        }

        const parsedVariant = JSON.parse(textContent);

        if (
          morphElementSelector === 'main' &&
          activeVariantPicker.dataset.templateProductMatch === 'true' &&
          parsedVariant?.id
        ) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(parsedVariant.id));
          history.replaceState({}, '', url.toString());
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (activeVariantPicker.selectedOptionId) {
          activeVariantPicker.dispatchEvent(
            new VariantUpdateEvent(parsedVariant, activeVariantPicker.selectedOptionId, {
              html,
              productId: activeVariantPicker.dataset.productId ?? '',
              newProduct,
              morphTarget: morphElementSelector,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Ensures there is always a selected option after morphing.
   * This avoids stale UI state when rapid changes remove the previously selected node.
   */
  #ensureSelectionState() {
    if (this.selectedOption) return;

    const firstSelect = this.querySelector('select');
    if (firstSelect instanceof HTMLSelectElement && firstSelect.options.length > 0) {
      const firstOption = firstSelect.options[0];
      if (!firstOption) return;
      firstSelect.value = firstOption.value;
      this.updateSelectedOption(firstSelect);
      return;
    }

    const firstEnabledInput = this.querySelector('fieldset input:not([aria-disabled="true"])');
    const firstInput = firstEnabledInput || this.querySelector('fieldset input');
    if (firstInput instanceof HTMLInputElement) {
      this.updateSelectedOption(firstInput);
    }
  }

  /**
   * Restores checked radio/select state from fetched HTML after a full main morph.
   * @param {HTMLElement} sourceVariantPicker
   */
  #syncSelectionFromSource(sourceVariantPicker) {
    sourceVariantPicker.querySelectorAll('fieldset input[checked]').forEach((sourceInput) => {
      if (!(sourceInput instanceof HTMLInputElement)) return;

      const targetInput = this.querySelector(`[data-option-value-id="${sourceInput.dataset.optionValueId}"]`);
      if (!(targetInput instanceof HTMLInputElement)) return;

      this.querySelectorAll(`input[name="${targetInput.name}"]`).forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.dataset.currentChecked = 'false';
        }
      });

      this.updateSelectedOption(targetInput);
    });

    sourceVariantPicker.querySelectorAll('select option[selected]').forEach((sourceOption) => {
      if (!(sourceOption instanceof HTMLOptionElement)) return;

      const select = this.querySelector(`select option[data-option-value-id="${sourceOption.dataset.optionValueId}"]`)?.closest('select');
      if (!(select instanceof HTMLSelectElement)) return;

      select.value = sourceOption.value;
      this.updateSelectedOption(select);
    });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#preserveInitialProductSwatchOrder();
    this.updateVariantPickerCss();
    this.#scrollCurrentSwatchesIntoView(prefersReducedMotion() ? 'instant' : 'smooth');

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;

        const productId = node.dataset.productId;
        if (productId) {
          return `${node.tagName}:${node.id || node.dataset.key || ''}:${productId}`;
        }

        return node.id || node.dataset.key;
      },
    });
  }

  /**
   * Updates document-level metadata that is outside the morphed main content.
   * @param {Document} newHtml
   */
  #syncDocumentTitle(newHtml) {
    const newTitle = newHtml.querySelector('title')?.textContent;
    if (newTitle) document.title = newTitle;
  }

  /** @param {Document} newHtml */
  #syncTemplateClasses(newHtml) {
    const currentTemplateClasses = Array.from(document.body.classList).filter((className) =>
      className.startsWith('template--')
    );
    const nextTemplateClasses = Array.from(newHtml.body.classList).filter((className) =>
      className.startsWith('template--')
    );

    document.body.classList.remove(...currentTemplateClasses);
    document.body.classList.add(...nextTemplateClasses);
  }

  /** @param {VariantPicker} picker @param {number} previousTop @param {number} previousScrollTop */
  #restorePickerViewportPosition(picker, previousTop, previousScrollTop) {
    const restore = () => {
      if (!picker.isConnected) return;

      if (previousScrollTop <= 1) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        return;
      }

      const scrollDelta = picker.getBoundingClientRect().top - previousTop;
      if (Math.abs(scrollDelta) >= 1) window.scrollBy({ top: scrollDelta, behavior: 'instant' });
    };

    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

/**
 * Forces product-bound custom elements to be replaced when a combined listing
 * changes product, while preserving stable keyed state within one product.
 * @param {Node | undefined} node
 * @returns {string | undefined}
 */
function getProductPageNodeKey(node) {
  if (!(node instanceof HTMLElement)) return undefined;

  const productId = node.dataset.productId;
  if (productId) return `${node.tagName}:${node.id || node.dataset.key || ''}:${productId}`;

  return node.id || node.dataset.key;
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
